import { msToDuration } from "@/lib/dateTime";
import type { DatabaseActionType } from "../../../../core/modules/Database/databaseTypes";
import { useOpenPlayerModal } from "@/hooks/playerModal";
import DateTimeCorrected from "@/components/DateTimeCorrected";

type Props = { action: DatabaseActionType; serverTime: number; tsFetch: number };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[8rem_1fr] gap-3 py-2.5 border-b border-white/[0.04] last:border-0 items-start">
            <dt className="text-[11px] font-mono font-bold uppercase tracking-widest text-slate-600 pt-0.5">
                {label}
            </dt>
            <dd className="text-sm text-slate-200 leading-snug">{children}</dd>
        </div>
    );
}

export default function ActionInfoTab({ action, serverTime, tsFetch }: Props) {
    const openPlayerModal = useOpenPlayerModal();

    // Ban expiration text
    let banExpiration: React.ReactNode = null;
    if (action.type === 'ban') {
        if (action.expiration === false) {
            banExpiration = <span className="text-[#ef4444] font-semibold">Permanent</span>;
        } else if (action.expiration > serverTime) {
            const dist = msToDuration((serverTime - action.expiration) * 1000, { units: ['mo', 'w', 'd', 'h', 'm'] });
            banExpiration = <span className="text-[#fbbf24]">In {dist}</span>;
        } else {
            banExpiration = (
                <DateTimeCorrected
                    className="text-slate-400 cursor-help"
                    serverTime={serverTime}
                    tsObject={action.expiration}
                    tsFetch={tsFetch}
                />
            );
        }
    }

    // Revocation text
    let revokedNode: React.ReactNode = <span className="text-slate-500">No</span>;
    if (action.revocation.timestamp) {
        revokedNode = (
            <span className="text-[#fbbf24]">
                By <span className="font-semibold text-slate-200">{action.revocation.author}</span> on{' '}
                <DateTimeCorrected
                    isDateOnly
                    className="cursor-help text-slate-300"
                    serverTime={serverTime}
                    tsObject={action.revocation.timestamp}
                    tsFetch={tsFetch}
                />
            </span>
        );
    }

    const targetLicenses = action.ids.filter(id => id.startsWith('license:'));
    const linkedLicense  = targetLicenses.length === 1 ? targetLicenses[0].split(':')[1] : null;

    return (
        <div className="flex flex-col gap-0">
            <dl>
                <Field label="Date">
                    <DateTimeCorrected
                        className="text-slate-300 cursor-help"
                        serverTime={serverTime}
                        tsObject={action.timestamp}
                        tsFetch={tsFetch}
                    />
                </Field>

                {action.type === 'ban' && (
                    <Field label="Expires">{banExpiration}</Field>
                )}

                {action.type === 'warn' && (
                    <Field label="Accepted">
                        {action.acked
                            ? <span className="text-slate-400">Yes</span>
                            : <span className="text-[#fbbf24]">Not yet</span>
                        }
                    </Field>
                )}

                <Field label="Revoked">{revokedNode}</Field>

                <Field label="Admin">
                    <span className="font-mono text-[#00ffd1]">{action.author}</span>
                </Field>

                <Field label="Player">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-200">
                            {action.playerName !== false
                                ? action.playerName
                                : <span className="italic text-slate-500">unknown player</span>
                            }
                        </span>
                        {linkedLicense && (
                            <button
                                onClick={() => openPlayerModal({ license: linkedLicense })}
                                className="shrink-0 px-2.5 py-1 rounded-lg border border-[#1f2d4d] bg-white/[0.03] text-[11px] font-semibold text-slate-400 hover:border-[#00ffd1]/25 hover:text-[#00ffd1] hover:bg-[#00ffd1]/5 transition-all font-mono uppercase tracking-wider"
                            >
                                View Player
                            </button>
                        )}
                    </div>
                </Field>
            </dl>

            {/* Reason */}
            <div className="mt-4">
                <div className="text-[11px] font-mono font-bold uppercase tracking-widest text-slate-600 mb-2">
                    Reason
                </div>
                <div className="w-full bg-black/30 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-slate-300 font-mono leading-relaxed whitespace-pre-wrap break-words min-h-[3rem]">
                    {action.reason}
                </div>
            </div>
        </div>
    );
}
